import { Box, Heading } from '@wix/design-system';
import { SparklesFilled } from '@wix/wix-ui-icons-common';
import React, { type CSSProperties } from 'react';

import { EditorContext, PluginsEventsContext, RicosContext } from './../../ricos-context';
import {
  Panel,
  PanelHeader,
  useClickOutside,
  usePanelContainerElement,
} from './../../ui-components';

import { AI_IMAGE_MODAL_ROOT } from '../data-hooks';
import type { DiscardPayload } from '../discard-confirmation-modal/discard-confirmation-modal';
import { isOfType } from '../ts-utils';
import type { AiPluginEditorConfig, ReferenceElement } from '../types';
import { AI_IMAGE_MODAL_ID, AI_TYPE } from '../types';
import { getSelectionAsText } from '../utils/get-selection-as-text';
import { Feed } from './feed';
import type { FooterRef } from './footer';
import { Footer } from './footer';
import { useAiImageModalApi } from './use-ai-image-modal-api';

type AiImageModalProps = {
  closeModal: () => void;
  openDiscardModal: (referenceElement: ReferenceElement) => Promise<DiscardPayload>;
  config: AiPluginEditorConfig;
  zIndex?: CSSProperties['zIndex'];
  modalSessionId: string;
  convertSelectionToPrompt?: boolean;
  insertActions: 'single' | 'both';
};

export const AiImageModal: React.FC<AiImageModalProps> = ({
  closeModal,
  openDiscardModal,
  config,
  modalSessionId,
  zIndex,
  convertSelectionToPrompt,
  insertActions,
}) => {
  const { t } = React.useContext(RicosContext);
  const { adapter } = React.useContext(EditorContext);
  const footerRef = React.useRef<FooterRef>(null);
  const selection = React.useMemo(() => getSelectionAsText(adapter.tiptapEditor), []);
  const containerEl = usePanelContainerElement();
  const resolvedZIndex = typeof zIndex === 'number' ? zIndex : 5500;

  const pluginsEvents = React.useContext(PluginsEventsContext);

  const publishModalAction = React.useMemo(() => {
    let counter = 0;
    return (actionName: string, value?: string, imageStyle?: string) => {
      counter++;
      pluginsEvents.publishModalAction({
        pluginId: AI_TYPE,
        modalId: AI_IMAGE_MODAL_ID,
        modalSessionId,
        actionName,
        value,
        actionNumber: counter,
        initialSelection: selection,
        style: imageStyle,
      });
    };
  }, [pluginsEvents.publishModalAction, modalSessionId]);

  const {
    state,
    goBack,
    retryPrompt,
    setPromptValue,
    setImageStyleValue,
    tryConvertingSelectionToPrompt,
    submitPrompt,
    cancelPrompt,
    insertImage,
    isConvertedPromptHelperVisible,
    closeOptimisedPromptHelper,
    tryCloseModal,
    isDiscardModalOpen,
  } = useAiImageModalApi({
    ...config,
    selection,
    closeModal,
    openDiscardModal,
    focusInputArea: () => footerRef.current?.focus(),
    modalRef: containerEl,
    convertSelectionToPrompt,
    publishModalAction,
  });

  const closeOnClickOutside = React.useCallback(() => {
    tryCloseModal();
    publishModalAction('close_on_click_outside');
  }, [tryCloseModal]);

  useClickOutside({
    containerRef: { current: containerEl },
    onClickOutside: closeOnClickOutside,
    disable: isDiscardModalOpen,
  });

  React.useEffect(() => {
    if (isOfType(['INITIAL'], state)) {
      setTimeout(() => {
        footerRef.current?.focus();
      }, 0);
    }
  }, [state.type]);

  React.useEffect(() => {
    if (convertSelectionToPrompt && state.type === 'INITIALIZING') {
      tryConvertingSelectionToPrompt(selection);
    }
  }, []);

  return (
    <Panel onCloseButtonClick={() => closeModal()} width={540} dataHook={AI_IMAGE_MODAL_ROOT}>
      <PanelHeader
        title={
          <Box gap="SP1">
            <Box color="B10">
              <SparklesFilled />
            </Box>
            <Heading size="medium">{t('AIPlugin_Modal_ImageGeneratorTitle')}</Heading>
          </Box>
        }
      />

      <Feed state={state} insertImage={insertImage} insertActions={insertActions} />
      <Footer
        ref={footerRef}
        state={state}
        zIndex={resolvedZIndex}
        modalRef={containerEl}
        setPromptValue={setPromptValue}
        cancelPrompt={cancelPrompt}
        setImageStyleValue={setImageStyleValue}
        retryPrompt={retryPrompt}
        goBack={goBack}
        submitPrompt={submitPrompt}
        isConvertedPromptHelperVisible={isConvertedPromptHelperVisible}
        closeOptimisedPromptHelper={closeOptimisedPromptHelper}
        publishModalAction={publishModalAction}
      />
    </Panel>
  );
};
